/* eslint-disable max-len */
import * as React from 'react'
import { useRef } from 'react'
import Root from './SingleSelectDropDownMenu.styles'
import { useDetectOutsideClicks } from '../../../util/useDetectOutsideClicks/useDetectOutsideClicks'
import SingleSelectDropDownInputField from '../SingleSelectDropDownInputField/SingleSelectDropDownInputField'
import { InputAttrs } from '../../types'

type Props = Omit<InputAttrs<string>, 'options'> & {
  id: string
  name?: string
  maxSuggestions?: number
  showOptions?: boolean
  placeholder?: string
  options: string[]
  className?: string
}

/**
 * SingleSelectDropDownMenu element
 * @param {object} props Component props
 * @param {string} props.id Element id
 * @param {Array<T>} props.value Value of the element
 * @param {function} props.onChange Callback on change
 * @param {Array<T>} props.options Options to select from
 * @param {number} props.maxSuggestions Maximum number of suggestions to show
 * @param {boolean} props.showOptions Initial expanded/collapsed state of the options
 * @param {string} props.placeholder Placeholder text
 * @param {boolean} props.isValid Whether the selected value/s are valid
 * @param {string} props.validationMessage Validation message
 * @param {boolean} props.isTouched User has interacted with the element
 * @param {boolean} props.disabled Disabled state
 * @param {string} props.className Additional class name
 */

const SingleSelectDropDownMenu = ({
  id,
  value,
  onChange,
  options,
  maxSuggestions,
  showOptions,
  placeholder,
  isValid,
  validationMessage,
  isTouched,
  disabled,
  className,
  name,
  ...rest
}: Props) => {
  const [selectedOption, setSelectedOption] = React.useState(value || '')
  const [isOpen, setIsOpen] = React.useState(showOptions)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [suggestions, setSuggestions] = React.useState(options.slice(0, maxSuggestions))
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = React.useState(0)

  let validationClass = ''
  if (isTouched) {
    validationClass = isValid ? 'valid' : 'invalid'
  }

  function onSelect(newValue) {
    setSelectedOption(newValue)
    setSearchQuery('')
    setSuggestions(options.slice(0, maxSuggestions))
    setIsOpen(false)
    onChange(newValue)
  }

  function onSearch(query) {
    setSearchQuery(query)
    setIsOpen(true)
    setSelectedSuggestionIndex(0)

    if (query.length === 0) {
      setSuggestions(options.slice(0, maxSuggestions))
      setSelectedOption('')
    } else {
      const filteredOptions = options
        .filter(option => option.toLowerCase().includes(query.toLowerCase()))
        .slice(0, maxSuggestions)
      setSuggestions(filteredOptions)
    }
  }

  function onSelectByIndex(e) {
    let suggestionIndex = 0

    if (e.key === 'ArrowDown') {
      suggestionIndex = selectedSuggestionIndex < suggestions.length - 1 ? selectedSuggestionIndex + 1 : 0
      setSelectedSuggestionIndex(suggestionIndex)
      setSelectedOption(suggestions[suggestionIndex])
    } else if (e.key === 'ArrowUp') {
      suggestionIndex = selectedSuggestionIndex > 0 ? selectedSuggestionIndex - 1 : suggestions.length - 1
      setSelectedSuggestionIndex(suggestionIndex)
      setSelectedOption(suggestions[suggestionIndex])
    } else if (e.key === 'Enter') {
      onSelect(selectedOption)
    }
  }

  function getHighlightedText(mainText, matchText) {
    const index = mainText.toLowerCase().indexOf(matchText.toLowerCase())
    const before = mainText.substring(0, index)
    const matchedText = mainText.substring(index, index + matchText.length)
    const after = mainText.substring(index + matchText.length)
    return (
      <span>
        <span className="highlighted">{before}</span>
        <span>{matchedText}</span>
        <span className="highlighted">{after}</span>
      </span>
    )
  }

  const ref = useRef(null)
  useDetectOutsideClicks(ref, () => setIsOpen(false))

  return (
    <Root
      onKeyUp={onSelectByIndex}
      className={`${className || ''} ${validationClass}`}
      ref={ref}
      disabled={disabled}
      {...rest}
    >
      <SingleSelectDropDownInputField
        id={id}
        name={name}
        value={searchQuery || selectedOption}
        onChange={onSearch}
        placeholder={placeholder || ''}
        isTouched={isTouched}
        isValid={isValid}
        validationMessage={validationMessage}
        disabled={disabled}
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => onSelect(value)}
      />
      {isOpen && (
        <ul>
          {suggestions.map((suggestion, index) => (
            <li
              key={`${index}-${suggestion}`}
              role="presentation"
              data-index={index}
              data-selected={selectedOption === suggestion}
              onClick={() => onSelect(suggestion)}
            >
              {getHighlightedText(suggestion, searchQuery)}
            </li>
          ))}
        </ul>
      )}
    </Root>
  )
}

SingleSelectDropDownMenu.defaultProps = {
  maxSuggestions: 10,
  showOptions: false,
  placeholder: 'Selecteer een van de opties',
  isValid: true,
  validationMessage: '',
  isTouched: false,
  disabled: false,
  className: '',
  name: '',
}

export default SingleSelectDropDownMenu
